import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import { WizardSteps, WizardContent } from "./ui/wizard-steps";
import {
  Globe,
  CheckCircle,
  AlertCircle,
  Loader2,
  Copy,
  ExternalLink,
  RefreshCw,
  HelpCircle,
  ChevronRight,
  ChevronLeft,
  Shield,
  Server,
  Trash2,
  Clock,
  Eye,
  EyeOff,
  Zap,
  Key,
  AlertTriangle
} from "lucide-react";
import { toast } from "sonner";

import { apiClient, formatP0Error } from '../lib/api';

export default function DomainSetup({ token, onDomainVerified }) {
  // Domain config state
  const [domainConfig, setDomainConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [saving, setSaving] = useState(false);

  // Wizard state
  const [showWizard, setShowWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [clientDomain, setClientDomain] = useState("");
  const [cloudflareToken, setCloudflareToken] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [tokenConsent, setTokenConsent] = useState(false);
  const [domainError, setDomainError] = useState("");
  const [setupInProgress, setSetupInProgress] = useState(false);
  const [setupResult, setSetupResult] = useState(null);

  // Info modal
  const [showInfo, setShowInfo] = useState(false);

  const wizardSteps = [
    { id: "domain", label: "Domaine" },
    { id: "tutorial", label: "Tutoriel" },
    { id: "token", label: "Token API" },
    { id: "create", label: "Création" }
  ];

  useEffect(() => {
    fetchDomainStatus();
  }, [token]);

  const fetchDomainStatus = async () => {
    try {
      // P0 HARDENING: Use centralized apiClient
      const response = await apiClient.get('/domain/status');
      setDomainConfig(response.data);
      if (response.data.client_domain) {
        setClientDomain(response.data.client_domain);
      }
    } catch (error) {
      console.error("Failed to fetch domain status:", error);
    } finally {
      setLoading(false);
    }
  };

  const validateDomain = (domain) => {
    const cleanDomain = domain.toLowerCase().trim()
      .replace(/^https?:\/\//, '')
      .split('/')[0];
    
    const pattern = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/;
    
    if (!pattern.test(cleanDomain)) {
      return { valid: false, error: "Format de domaine invalide" };
    }
    
    const blocked = ['preview.', '.vercel.', '.netlify.', '.herokuapp.'];
    for (const b of blocked) {
      if (cleanDomain.includes(b)) {
        return { valid: false, error: `Les domaines ${b.replace(/\./g, '')} ne sont pas autorisés` };
      }
    }
    
    return { valid: true, domain: cleanDomain };
  };

  const handleDomainChange = (value) => {
    setClientDomain(value);
    if (value.length > 3) {
      const validation = validateDomain(value);
      setDomainError(validation.valid ? "" : validation.error);
    } else {
      setDomainError("");
    }
  };

  const handleCloudflareSetup = async () => {
    if (!cloudflareToken || !tokenConsent) {
      toast.error("Veuillez entrer le token et accepter les conditions");
      return;
    }

    setSetupInProgress(true);
    setSetupResult(null);

    try {
      // P0 HARDENING: Use centralized apiClient
      const response = await apiClient.post('/domain/setup', {
        client_domain: clientDomain,
        setup_mode: "cloudflare",
        cloudflare_token: cloudflareToken
      });

      setSetupResult(response.data);

      if (response.data.success && !response.data.fallback_to_manual) {
        toast.success("Sous-domaine créé avec succès !");
        setWizardStep(3);
        fetchDomainStatus();
        // Notify parent to refresh callback URLs
        if (onDomainVerified) {
          onDomainVerified();
        }
      } else if (response.data.fallback_to_manual) {
        toast.error(response.data.message || "Erreur Cloudflare. Utilisez le mode manuel.");
      } else {
        toast.error(response.data.message || "Erreur lors de la création");
      }
    } catch (error) {
      const errorMsg = formatP0Error(error) || "Erreur lors de la configuration";
      toast.error(errorMsg);
      setSetupResult({ success: false, message: errorMsg });
    } finally {
      setSetupInProgress(false);
    }
  };

  const handleManualSetup = async () => {
    setSaving(true);
    try {
      // P0 HARDENING: Use centralized apiClient
      const response = await apiClient.post('/domain/setup', {
        client_domain: clientDomain,
        setup_mode: "manual"
      });

      if (response.data.success) {
        toast.success("Configuration initiée !");
        setDomainConfig({
          has_domain: true,
          ...response.data
        });
        setShowWizard(false);
        fetchDomainStatus();
      }
    } catch (error) {
      toast.error(formatP0Error(error) || "Erreur lors de la configuration");
    } finally {
      setSaving(false);
    }
  };

  const handleVerify = async () => {
    setVerifying(true);
    try {
      // P0 HARDENING: Use centralized apiClient
      const response = await apiClient.post('/domain/verify', { check_https: true });

      if (response.data.success) {
        toast.success(response.data.status_message || "Domaine vérifié !");
        // Notify parent to refresh callback URLs when domain is verified
        if (onDomainVerified && (response.data.status === "dns_verified" || response.data.status === "https_ready" || response.data.status === "completed")) {
          onDomainVerified();
        }
      } else {
        toast.error(response.data.status_message || "Vérification échouée");
      }
      
      fetchDomainStatus();
    } catch (error) {
      toast.error(formatP0Error(error) || "Erreur lors de la vérification");
    } finally {
      setVerifying(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer la configuration de domaine ?")) return;
    
    try {
      // P0 HARDENING: Use centralized apiClient
      await apiClient.delete('/domain');
      toast.success("Configuration supprimée");
      setDomainConfig(null);
      setShowWizard(false);
      setWizardStep(0);
      setClientDomain("");
      setCloudflareToken("");
      setTokenConsent(false);
      setSetupResult(null);
    } catch (error) {
      toast.error(formatP0Error(error) || "Erreur lors de la suppression");
    }
  };

  const handleRevokeCloudflare = async () => {
    if (!window.confirm("Révoquer l'accès Cloudflare ? L'enregistrement DNS restera en place.")) return;
    
    try {
      // P0 HARDENING: Use centralized apiClient
      await apiClient.post('/domain/revoke-cloudflare', {});
      toast.success("Accès Cloudflare révoqué");
      fetchDomainStatus();
    } catch (error) {
      toast.error(formatP0Error(error) || "Erreur lors de la révocation");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copié !");
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      not_configured: { color: "bg-gray-100 text-gray-600", icon: AlertCircle, text: "Non configuré" },
      pending: { color: "bg-yellow-100 text-yellow-700", icon: Clock, text: "En attente" },
      dns_created: { color: "bg-blue-100 text-blue-700", icon: Server, text: "DNS créé" },
      dns_verified: { color: "bg-blue-100 text-blue-700", icon: CheckCircle, text: "DNS vérifié" },
      https_ready: { color: "bg-green-100 text-green-700", icon: Shield, text: "HTTPS prêt" },
      completed: { color: "bg-green-100 text-green-700", icon: CheckCircle, text: "Opérationnel" },
      error: { color: "bg-red-100 text-red-700", icon: AlertCircle, text: "Erreur" }
    };
    
    const config = statusConfig[status] || statusConfig.not_configured;
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} gap-1`}>
        <Icon className="w-3 h-3" />
        {config.text}
      </Badge>
    );
  };

  // Clean domain for display
  const cleanDomain = clientDomain.toLowerCase().replace(/^https?:\/\//, '').split('/')[0];
  const generatedSubdomain = cleanDomain ? `ai-marketing-engine.${cleanDomain}` : '';

  // Render wizard steps
  const renderWizardStep = () => {
    switch (wizardStep) {
      case 0: // Domain input
        return (
          <div className="space-y-6">
            <div>
              <Label className="text-base font-medium">Quel est votre domaine ?</Label>
              <p className="text-sm text-muted-foreground mt-1 mb-3">
                Entrez le domaine de votre site principal (celui géré par Cloudflare)
              </p>
              <Input
                value={clientDomain}
                onChange={(e) => handleDomainChange(e.target.value)}
                placeholder="exemple.com"
                className={domainError ? "border-red-500" : ""}
              />
              {domainError && (
                <p className="text-sm text-red-500 mt-1">{domainError}</p>
              )}
            </div>
            
            {cleanDomain && !domainError && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800 mb-1">
                  <strong>Nous allons créer :</strong>
                </p>
                <p className="font-mono text-blue-900 text-lg">
                  {generatedSubdomain}
                </p>
                <p className="text-xs text-blue-600 mt-2">
                  Ce sous-domaine sera utilisé pour les intégrations OAuth (Facebook, etc.)
                </p>
              </div>
            )}
          </div>
        );

      case 1: // Cloudflare Tutorial
        return (
          <div className="space-y-6">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <img src="https://www.cloudflare.com/favicon.ico" alt="Cloudflare" className="w-5 h-5" />
                <h3 className="font-semibold text-orange-900">Créer un API Token Cloudflare</h3>
                <Badge className="bg-orange-100 text-orange-700 text-xs">~2 minutes</Badge>
              </div>
              
              <ol className="space-y-3 text-sm text-orange-800">
                <li className="flex gap-2">
                  <span className="font-bold">1.</span>
                  <span>
                    Ouvrez <a href="https://dash.cloudflare.com/profile/api-tokens" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">dash.cloudflare.com/profile/api-tokens</a>
                  </span>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold">2.</span>
                  <span>Cliquez sur <strong>"Create Token"</strong></span>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold">3.</span>
                  <span>Choisissez <strong>"Create Custom Token"</strong></span>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold">4.</span>
                  <div>
                    <span>Configurez les permissions :</span>
                    <div className="bg-white/50 rounded p-2 mt-1 font-mono text-xs">
                      <div>Zone → DNS → <strong>Edit</strong></div>
                      <div>Zone → Zone → <strong>Read</strong></div>
                    </div>
                  </div>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold">5.</span>
                  <div>
                    <span>Zone Resources :</span>
                    <div className="bg-white/50 rounded p-2 mt-1 font-mono text-xs">
                      Include → Specific zone → <strong>{cleanDomain || "votre-domaine.com"}</strong>
                    </div>
                  </div>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold">6.</span>
                  <span>Cliquez <strong>"Continue to summary"</strong> puis <strong>"Create Token"</strong></span>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold">7.</span>
                  <span>Copiez le token généré (il ne sera plus affiché)</span>
                </li>
              </ol>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <Shield className="w-5 h-5 text-green-600 mt-0.5" />
                <div className="text-sm text-green-800">
                  <strong>Sécurité :</strong> Ce token ne permet QUE de créer/modifier les DNS de {cleanDomain || "votre domaine"}. 
                  Aucun accès à vos autres services. Vous pourrez le révoquer à tout moment.
                </div>
              </div>
            </div>

            <div className="flex justify-center">
              <Button
                variant="outline"
                onClick={() => window.open("https://dash.cloudflare.com/profile/api-tokens", "_blank")}
                className="gap-2"
              >
                <ExternalLink className="w-4 h-4" />
                Ouvrir Cloudflare
              </Button>
            </div>
          </div>
        );

      case 2: // Token input
        return (
          <div className="space-y-6">
            <div>
              <Label className="text-base font-medium">Cloudflare API Token</Label>
              <p className="text-sm text-muted-foreground mt-1 mb-3">
                Collez le token que vous venez de créer
              </p>
              <div className="relative">
                <Input
                  type={showToken ? "text" : "password"}
                  value={cloudflareToken}
                  onChange={(e) => setCloudflareToken(e.target.value)}
                  placeholder="Votre token Cloudflare..."
                  className="pr-10 font-mono"
                />
                <button
                  type="button"
                  onClick={() => setShowToken(!showToken)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-start space-x-2">
              <Checkbox
                id="consent"
                checked={tokenConsent}
                onCheckedChange={setTokenConsent}
              />
              <label
                htmlFor="consent"
                className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Je comprends que je peux révoquer ce token à tout moment depuis mon tableau de bord Cloudflare
              </label>
            </div>

            {/* Summary of what will be created */}
            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <h4 className="font-medium text-sm">Récapitulatif</h4>
              <div className="text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Sous-domaine :</span>
                  <span className="font-mono">{generatedSubdomain}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Type :</span>
                  <span>CNAME</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Proxy Cloudflare :</span>
                  <span className="text-orange-600">OFF (DNS only)</span>
                </div>
              </div>
            </div>

            {setupResult && !setupResult.success && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                  <div className="text-sm text-red-800">
                    <strong>Erreur :</strong> {setupResult.message}
                    {setupResult.fallback_to_manual && (
                      <p className="mt-1">
                        Vous pouvez utiliser le mode manuel à la place.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 3: // Success / Creation result
        return (
          <div className="space-y-6">
            {setupResult?.success ? (
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-green-800">Sous-domaine créé avec succès !</h3>
                <p className="text-muted-foreground mt-2">
                  La propagation DNS peut prendre quelques minutes.
                </p>
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-4">
                  <p className="font-mono text-green-800">
                    https://{generatedSubdomain}
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <Loader2 className="w-12 h-12 animate-spin text-blue-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold">Création en cours...</h3>
                <p className="text-muted-foreground mt-2">
                  Veuillez patienter pendant la création de l'enregistrement DNS
                </p>
              </div>
            )}

            {/* Cloudflare steps progress */}
            {setupResult?.cloudflare_result?.steps && (
              <div className="space-y-2">
                <h4 className="font-medium text-sm">Étapes Cloudflare</h4>
                {setupResult.cloudflare_result.steps.map((step, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    {step.status === "success" && <CheckCircle className="w-4 h-4 text-green-500" />}
                    {step.status === "error" && <AlertCircle className="w-4 h-4 text-red-500" />}
                    {step.status === "in_progress" && <Loader2 className="w-4 h-4 animate-spin text-blue-500" />}
                    {step.status === "warning" && <AlertTriangle className="w-4 h-4 text-yellow-500" />}
                    <span className={step.status === "error" ? "text-red-600" : ""}>{step.name}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Callback URLs */}
            {setupResult?.callback_urls && setupResult.success && (
              <div className="space-y-3 pt-4 border-t">
                <h4 className="font-medium">URLs pour Meta (Facebook)</h4>
                <p className="text-sm text-muted-foreground">
                  Copiez ces URLs dans la configuration de votre App Meta/Facebook
                </p>
                <div className="bg-muted/50 rounded-lg p-4 space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">App Domain :</span>
                    <div className="flex items-center gap-2">
                      <code className="text-xs">{setupResult.callback_urls.facebook?.app_domains}</code>
                      <button onClick={() => copyToClipboard(setupResult.callback_urls.facebook?.app_domains)}>
                        <Copy className="w-3.5 h-3.5 text-muted-foreground hover:text-foreground" />
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Website URL :</span>
                    <div className="flex items-center gap-2">
                      <code className="text-xs">{setupResult.callback_urls.facebook?.website_url}</code>
                      <button onClick={() => copyToClipboard(setupResult.callback_urls.facebook?.website_url)}>
                        <Copy className="w-3.5 h-3.5 text-muted-foreground hover:text-foreground" />
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Redirect URI :</span>
                    <div className="flex items-center gap-2">
                      <code className="text-xs">{setupResult.callback_urls.facebook?.redirect_uri}</code>
                      <button onClick={() => copyToClipboard(setupResult.callback_urls.facebook?.redirect_uri)}>
                        <Copy className="w-3.5 h-3.5 text-muted-foreground hover:text-foreground" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`border-2 ${domainConfig?.status === 'completed' ? 'border-green-300' : domainConfig?.has_domain ? 'border-blue-300' : 'border-gray-200'}`}>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gradient-to-br from-orange-500 to-red-600">
              <Globe className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <CardTitle className="text-lg">Domaine – AI MARKETING ENGINE</CardTitle>
                <Badge variant="outline" className="text-xs">Cloudflare</Badge>
                <button
                  onClick={() => setShowInfo(!showInfo)}
                  className="p-1 rounded-full hover:bg-muted/80 transition-colors"
                  title="Plus d'informations"
                >
                  <AlertCircle className="w-4 h-4 text-blue-500" />
                </button>
              </div>
              <CardDescription>
                Sous-domaine dédié pour les intégrations OAuth (Facebook, etc.)
              </CardDescription>
            </div>
          </div>
          {domainConfig?.has_domain && getStatusBadge(domainConfig.status)}
        </div>
        
        {/* Info panel */}
        {showInfo && (
          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
            <h4 className="font-semibold text-blue-900 mb-2">Pourquoi un sous-domaine dédié ?</h4>
            <p className="text-blue-800 mb-2">
              Ce sous-domaine est nécessaire pour sécuriser l'authentification Facebook/Meta et ne modifie pas votre site principal.
            </p>
            <ul className="space-y-1 text-blue-700 text-xs">
              <li>• Meta/Facebook exige un domaine stable et vérifié pour OAuth</li>
              <li>• Les domaines partagés (*.preview.*) sont souvent bloqués</li>
              <li>• URL professionnelle : ai-marketing-engine.votredomaine.com</li>
            </ul>
          </div>
        )}
      </CardHeader>

      <CardContent>
        {/* Show wizard or current config */}
        {showWizard ? (
          <div className="space-y-6">
            <WizardSteps steps={wizardSteps} currentStep={wizardStep} />
            
            <WizardContent>
              {renderWizardStep()}
            </WizardContent>
            
            <div className="flex justify-between pt-4 border-t">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    if (wizardStep === 0) {
                      setShowWizard(false);
                      setSetupResult(null);
                    } else {
                      setWizardStep(wizardStep - 1);
                    }
                  }}
                  disabled={setupInProgress}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  {wizardStep === 0 ? "Annuler" : "Retour"}
                </Button>
                
                {wizardStep === 0 && (
                  <Button
                    variant="ghost"
                    onClick={handleManualSetup}
                    disabled={!clientDomain || domainError || saving}
                    className="text-muted-foreground"
                  >
                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Mode manuel
                  </Button>
                )}
              </div>
              
              {wizardStep === 0 && (
                <Button
                  onClick={() => setWizardStep(1)}
                  disabled={!clientDomain || domainError}
                  className="gap-2 bg-orange-500 hover:bg-orange-600"
                >
                  <Zap className="w-4 h-4" />
                  Auto-setup Cloudflare
                  <ChevronRight className="w-4 h-4" />
                </Button>
              )}
              
              {wizardStep === 1 && (
                <Button onClick={() => setWizardStep(2)}>
                  J'ai créé mon token
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
              
              {wizardStep === 2 && (
                <Button
                  onClick={handleCloudflareSetup}
                  disabled={!cloudflareToken || !tokenConsent || setupInProgress}
                  className="gap-2 bg-orange-500 hover:bg-orange-600"
                >
                  {setupInProgress ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Zap className="w-4 h-4" />
                  )}
                  Créer le sous-domaine maintenant
                </Button>
              )}
              
              {wizardStep === 3 && setupResult?.success && (
                <Button onClick={() => { setShowWizard(false); fetchDomainStatus(); }}>
                  Terminé
                </Button>
              )}
            </div>
          </div>
        ) : domainConfig?.has_domain ? (
          <div className="space-y-4">
            {/* Current configuration */}
            <div className="bg-muted/50 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Domaine client</span>
                <span className="font-medium">{domainConfig.client_domain}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Sous-domaine</span>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm">{domainConfig.subdomain}</span>
                  {(domainConfig.status === "completed" || domainConfig.status === "dns_verified") && (
                    <a 
                      href={`https://${domainConfig.subdomain}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-500 hover:text-blue-600"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Cible CNAME</span>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs">{domainConfig.cname_target}</span>
                  <button onClick={() => copyToClipboard(domainConfig.cname_target)}>
                    <Copy className="w-3.5 h-3.5 text-muted-foreground hover:text-foreground" />
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between pt-2 border-t">
                <span className="text-sm text-muted-foreground">Mode</span>
                <Badge variant="outline" className="text-xs">
                  {domainConfig.setup_mode === "cloudflare" ? "Cloudflare auto" : "Manuel"}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">DNS vérifié</span>
                {domainConfig.dns_verified ? (
                  <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" />Oui</Badge>
                ) : (
                  <Badge variant="outline" className="text-yellow-600"><AlertCircle className="w-3 h-3 mr-1" />Non</Badge>
                )}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">HTTPS</span>
                {domainConfig.https_ready ? (
                  <Badge className="bg-green-100 text-green-700"><Shield className="w-3 h-3 mr-1" />Prêt</Badge>
                ) : (
                  <Badge variant="outline" className="text-yellow-600"><Clock className="w-3 h-3 mr-1" />En attente</Badge>
                )}
              </div>
            </div>
            
            {/* Logs */}
            {domainConfig.logs && domainConfig.logs.length > 0 && (
              <Accordion type="single" collapsible>
                <AccordionItem value="logs">
                  <AccordionTrigger className="text-sm">
                    Logs d'installation ({domainConfig.logs.length})
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                      {domainConfig.logs.slice().reverse().map((log, i) => (
                        <div key={i} className="text-xs flex items-start gap-2 py-1">
                          <span className="text-muted-foreground whitespace-nowrap">
                            {new Date(log.timestamp).toLocaleTimeString('fr-FR')}
                          </span>
                          {log.status === 'success' && <CheckCircle className="w-3 h-3 text-green-500 mt-0.5" />}
                          {log.status === 'error' && <AlertCircle className="w-3 h-3 text-red-500 mt-0.5" />}
                          {log.status === 'info' && <AlertCircle className="w-3 h-3 text-blue-500 mt-0.5" />}
                          <span className={`${log.status === 'success' ? 'text-green-600' : log.status === 'error' ? 'text-red-600' : 'text-muted-foreground'}`}>
                            {log.message}
                          </span>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            )}
            
            {/* Callback URLs for Meta */}
            {domainConfig.callback_urls && (domainConfig.status === "completed" || domainConfig.dns_verified) && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">URLs pour Meta (Facebook)</Label>
                <div className="bg-muted/50 rounded-lg p-3 space-y-2 text-xs">
                  <div className="flex items-center justify-between">
                    <span>App Domain</span>
                    <div className="flex items-center gap-1">
                      <code>{domainConfig.callback_urls.facebook?.app_domains}</code>
                      <button onClick={() => copyToClipboard(domainConfig.callback_urls.facebook?.app_domains)}>
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Website URL</span>
                    <div className="flex items-center gap-1">
                      <code>{domainConfig.callback_urls.facebook?.website_url}</code>
                      <button onClick={() => copyToClipboard(domainConfig.callback_urls.facebook?.website_url)}>
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Redirect URI</span>
                    <div className="flex items-center gap-1">
                      <code>{domainConfig.callback_urls.facebook?.redirect_uri}</code>
                      <button onClick={() => copyToClipboard(domainConfig.callback_urls.facebook?.redirect_uri)}>
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <Button onClick={handleVerify} disabled={verifying} variant="outline" className="flex-1 gap-2">
                {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                Re-vérifier
              </Button>
              {domainConfig.setup_mode === "cloudflare" && (
                <Button onClick={handleRevokeCloudflare} variant="outline" className="gap-2" title="Révoquer l'accès Cloudflare">
                  <Key className="w-4 h-4" />
                </Button>
              )}
              <Button onClick={handleDelete} variant="destructive" size="icon">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Configurez un sous-domaine dédié pour sécuriser vos intégrations OAuth (Facebook, etc.)
            </p>
            
            <div className="flex flex-col gap-2">
              <Button onClick={() => setShowWizard(true)} className="gap-2 bg-orange-500 hover:bg-orange-600">
                <Zap className="w-4 h-4" />
                Créer automatiquement mon sous-domaine (Cloudflare)
              </Button>
              <Button variant="outline" onClick={() => { setShowWizard(true); }} className="gap-2">
                <HelpCircle className="w-4 h-4" />
                Configurer manuellement
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
